import type { CatalogItem, Tenant } from "@albertine/types/openapi/lmt";
import classNames from "classnames";
import { useRef, useState } from "react";
import { EventEmitter } from "../../EventEmitter";
import ArticleSearchInput from "../CatalogBeta/ArticleSearchInput";
import ArticleThumbnail from "../CatalogBeta/ArticleThumbnail";
import Button from "./Buttons";
import { Flex } from "./Flex";
import Icon from "./Icon";
import SortableList, { type SortableListActions } from "./list/SortableList";

const SortableArticles = SortableList<CatalogItem>();

type SelectArticlesProps = {
    title?: string;
    initialArticles: CatalogItem[];
    onChange: (newArticles: CatalogItem[]) => void;
    disabled?: boolean;
    discoverableForTenant?: Tenant | "all";
};

function SelectArticles(props: SelectArticlesProps) {
    const {
        title,
        initialArticles,
        onChange,
        disabled = false,
        discoverableForTenant,
    } = props;

    const [tempArticles, setTempArticles] = useState<CatalogItem[]>(initialArticles);

    const sortableInputRef = useRef<SortableListActions<CatalogItem>>(null);

    return (
        <Flex.Column gap="medium">
            {title && <h4>{title}</h4>}

            {!disabled && (
                <ArticleSearchInput
                    label="New article"
                    searchMode="only-published"
                    discoverableForTenant={discoverableForTenant}
                    onSelect={(selection) => {
                        if (!tempArticles.map((it) => it.id).includes(selection.id)) {
                            const newSortableArticles = [...tempArticles, selection];

                            setTempArticles(newSortableArticles);
                            sortableInputRef.current?.updateValues(newSortableArticles);
                        } else {
                            EventEmitter.emit({
                                name: "showToastMessage",
                                params: {
                                    message: "Items already selected",
                                    type: "error",
                                },
                            });
                        }
                    }}
                />
            )}

            <SortableArticles
                ref={sortableInputRef}
                initialItems={tempArticles}
                onChange={(sortedItems) => {
                    setTempArticles(sortedItems);
                    onChange(sortedItems);
                }}
                renderItem={(article) => (
                    <Flex.Row
                        gap="medium"
                        className={classNames("theme-form__sortable-container", {
                            "theme-form__sortable-container--unpublished":
                                !article.published,
                        })}
                    >
                        <Icon.DragIndicator.Small.N50 />
                        <ArticleThumbnail article={article} size="M" />
                        <Button.Ghost
                            disabled={disabled}
                            onClick={() => {
                                if (!disabled) {
                                    sortableInputRef.current?.updateValues(
                                        tempArticles.filter(
                                            (it) => it.id !== article.id,
                                        ),
                                    );
                                }
                            }}
                        >
                            <Icon.Close.Medium.N10 />
                        </Button.Ghost>
                    </Flex.Row>
                )}
                disabled={disabled}
            />
        </Flex.Column>
    );
}

export default SelectArticles;
